import { Content } from "./Controls/Content";
import { Container, Row, Col } from "react-bootstrap";
import { CheckCircleFill, XCircleFill } from "react-bootstrap-icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState, ReactNode } from "react";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "./Data/ApiStripe";
import { PaymentIntent } from "@stripe/stripe-js";
import { useAtom } from "jotai";
import { authAtom, SpotSyncAuth } from "./Data/Atoms";
import { Button } from "./Controls/Button";
import { createApiService } from "./shared/apiConfig";
import { FacilityApi } from "./shared/api/axios-client";
import "./UserPlanPaidPage.scss";

export interface PaymentStatusProps {
    pmtIntentKey:          string;
    pmtIntentClientSecret: string;
    redirectStatus:        string;
    returnTo:              string;
    returnLabel:           string;
    source?:               string;
    reserveConfirmTxt?:    string | null;
}

export function status( status: PaymentIntent.Status ) {
    switch( status ) {
        case "canceled":                return "Canceled";
        case "processing":              return "Processing";
        case "requires_action":         return "Requires Action";
        case "requires_capture":        return "Hold Successful";
        case "requires_confirmation":   return "Requires Confirmation";
        case "requires_payment_method": return "Requires Payment Method";
        case "succeeded":               return "Succeeded";
        default:                        return "Unrecognized Payment Status";
    }
}

export function confirmationEmailMessage( email?: string | null ): ReactNode {
    const trimmed = email?.trim();
    if ( !trimmed ) {
        return "You will receive an email shortly with your confirmation.";
    }
    return <>An email confirmation has been sent to <strong className="payment-success-email">{trimmed}</strong>.</>;
}

export function resolveParkerEmail( auth: SpotSyncAuth ): string | undefined {
    const loggedInEmail = auth.account?.emailAddress?.trim();
    if ( loggedInEmail ) {
        return loggedInEmail;
    }
    return localStorage.getItem( "checkoutGuestEmail" )?.trim() || undefined;
}

function escapeHtml( value: string ) {
    return value
        .replace( /&/g, "&amp;" )
        .replace( /</g, "&lt;" )
        .replace( />/g, "&gt;" )
        .replace( /"/g, "&quot;" );
}

/** Substitutes {{ emailAddress }} in facility ReserveConfirmTxt HTML. */
export function applyReserveConfirmPlaceholders( html: string, email?: string | null ): string {
    const value = escapeHtml( email?.trim() ?? "" );
    return html.replace( /\{\{\s*emailAddress\s*\}\}/gi, value );
}

export function description( status: PaymentIntent.Status, source?: string, email?: string ): ReactNode[] {
    switch( status ) {
        case "canceled":                return ["Canceled"];
        case "processing":              return ["Your payment is processing with your bank and will take several days to settle."];
        case "requires_action":         return ["You will be emailed instructions for additional information needed to verify your bank account.","Your parking subscription has been submitted for approval."];
        case "requires_capture":        return ["We have placed a temporary hold on your payment card. You have NOT been charged.",
                                                source === "hourly" 
                                                    ? "Payment will be captured once your session is complete."
                                                    : "If a staff member approves your application the payment will be captured."];
        case "requires_confirmation":   return ["Requires Confirmation"];
        case "requires_payment_method": return ["Requires Payment Method"];
        case "succeeded":               return ["We have successfully received your payment!",
                                                confirmationEmailMessage( email )];
        default:                        return ["Unrecognized Payment Status"];
    }
}

function isSuccessStatus(status: PaymentIntent.Status): boolean {
    return ["succeeded", "processing", "requires_capture", "requires_action"].includes(status);
}

function PaymentSuccessCard( { msg, desc, returnTo, returnLabel, reserveConfirmTxt }: { msg: string, desc: ReactNode[], returnTo: string, returnLabel: string, reserveConfirmTxt?: string | null } ) {
    const nav = useNavigate();
    const [ auth ] = useAtom( authAtom );
    const customHtml = reserveConfirmTxt
        ? applyReserveConfirmPlaceholders( reserveConfirmTxt, resolveParkerEmail( auth ) )
        : null;

    return (
        <Container fluid className="px-0 px-sm-2 pt-0 pt-sm-2">
            <Row className="justify-content-center mt-0 mt-sm-4 g-0">
                <Col xs={12} md={7} lg={5} className="px-0 px-sm-3">
                    <div className="payment-success-card">
                        <div className="payment-success-header text-center">
                            <div className="payment-success-icon-wrap">
                                <div className="ring" />
                                <div className="ring" />
                                <div className="icon-circle">
                                    <CheckCircleFill color="#D61F84" size={48} />
                                </div>
                            </div>
                            <div className="payment-success-title">{msg}</div>
                            <div className="payment-success-wave">
                                <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="white" />
                                </svg>
                            </div>
                        </div>
                        <div className="payment-success-body text-center">
                            {customHtml ? (
                                <div
                                    className="payment-success-confirm-html"
                                    dangerouslySetInnerHTML={{ __html: customHtml }}
                                />
                            ) : (
                                desc.map((d, i) => (
                                    <p key={i} className="payment-success-desc mb-2">{d}</p>
                                ))
                            )}
                            {auth.isLoggedIn && (
                                <div className="payment-success-btn mt-5">
                                    <Button className="px-5 btn-main" onClick={() => nav(returnTo)}>
                                        {returnLabel}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

function FreeCheckoutSuccess( { returnTo, returnLabel, reserveConfirmTxt }: { returnTo: string, returnLabel: string, reserveConfirmTxt?: string | null } ) {
    const [ auth ] = useAtom( authAtom );
    return <PaymentSuccessCard
        msg="Reservation Confirmed"
        desc={[
            "Your reservation is confirmed.",
            confirmationEmailMessage( resolveParkerEmail( auth ) ),
        ]}
        returnTo={returnTo}
        returnLabel={returnLabel}
        reserveConfirmTxt={reserveConfirmTxt}
    />;
}

export function PaymentStatus( { pmtIntentClientSecret, returnTo, returnLabel, source, reserveConfirmTxt }: PaymentStatusProps ) {

    const stripe = useStripe();
    const nav = useNavigate();
    const [msg, setMsg] = useState<string>();
    const [desc, setDesc] = useState<ReactNode[]>( [] );
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [ auth, setAuth ] = useAtom( authAtom );
    const [isLoaded, setIsLoaded] = useState(false);

    const load = async () => {
        if( !stripe || !pmtIntentClientSecret ) {
            return;
        }

        // retrieve the paymentintent
        const { paymentIntent } = await stripe.retrievePaymentIntent( pmtIntentClientSecret )

        if( !paymentIntent ) {         
            return;
        }


        // Inspect the PaymentIntent `status` to indicate the status of the payment
        // to your customer.
        // Some payment methods will [immediately succeed or fail][0] upon
        // confirmation, while others will first enter a `processing` state.
        // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
        
        setMsg(status(paymentIntent.status));
        setDesc(description(paymentIntent.status, source, resolveParkerEmail( auth )));
        setIsSuccess(isSuccessStatus(paymentIntent.status));
        setIsLoaded(true);
    }

    useEffect( () => {
        load();
    }, [stripe] );

    if (!isLoaded) return <Content fluid />;

    if (isSuccess) {
        return <PaymentSuccessCard msg={msg!} desc={desc} returnTo={returnTo} returnLabel={returnLabel} reserveConfirmTxt={reserveConfirmTxt} />;
    }

    return (
        <Container fluid className="px-0 px-sm-2 pt-0 pt-sm-2">
            <Row className="justify-content-center mt-0 mt-sm-5 g-0">
                <Col xs={12} md={7} lg={5} className="px-0 px-sm-3">
                    <div className="payment-error-card">
                        <div className="payment-error-header">
                            <XCircleFill size={56} color="white" className="mb-3" />
                            <h3 className="fw-bold mb-0">{msg}</h3>
                        </div>
                        <div className="p-4 text-center bg-white rounded-bottom">
                            {desc.map((d, i) => (
                                <p key={i} className="text-secondary mb-2">{d}</p>
                            ))}
                            {auth.isLoggedIn && (
                                <Button className="mt-3 px-5" onClick={() => nav(returnTo)}>
                                    {returnLabel}
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export function UserPaidPage() {
    //?payment_intent=pi_3QOsYpRR5LM9CxtV1Hv8VGP5&payment_intent_client_secret=pi_3QOsYpRR5LM9CxtV1Hv8VGP5_secret_lOuQxrDKeH6lkDQXFoMDfrg15&redirect_status=succeeded
    const [searchParams] = useSearchParams();
    const pmtIntentKey          = searchParams.get( "payment_intent"               );
    const pmtIntentClientSecret = searchParams.get( "payment_intent_client_secret" );
    const redirectStatus        = searchParams.get( "redirect_status"              );
    const source = searchParams.get("source") ?? undefined;
    const returnTo = source === "hourly" ? "/user/reservation/upcoming" : (searchParams.get("returnTo") ?? "/user/profile"); 
    const returnLabel = source === "hourly" ? "View Reservations" : (searchParams.get("returnLabel") ?? "Go to Profile");
    const isFreeCheckout = searchParams.get("free") === "1";
    const facilityIdParam = searchParams.get("facilityId");
    const facilityId = facilityIdParam ? Number(facilityIdParam) : NaN;
    const [reserveConfirmTxt, setReserveConfirmTxt] = useState<string | null>(null);

    useEffect(() => {
        if (source !== "hourly" || !Number.isFinite(facilityId) || facilityId <= 0) {
            return;
        }
        const facilityApi = createApiService(FacilityApi);
        facilityApi.getFacilityLite({ facilityId }).then(res => {
            const txt = res.data.reserveConfirmTxt?.trim();
            if (txt) {
                setReserveConfirmTxt(txt);
            }
        }).catch(() => {
            // keep default confirmation copy
        });
    }, [source, facilityId]);

    if ( isFreeCheckout ) {
        return <FreeCheckoutSuccess returnTo={returnTo} returnLabel={returnLabel} reserveConfirmTxt={reserveConfirmTxt} />;
    }

    return <Elements stripe={stripePromise}>
        <PaymentStatus pmtIntentClientSecret={pmtIntentClientSecret!}
                       pmtIntentKey={pmtIntentKey!}
                       redirectStatus={redirectStatus!}
                       returnTo={returnTo} 
                       returnLabel={returnLabel}
                       source={source}
                       reserveConfirmTxt={reserveConfirmTxt}/>
    </Elements>
}